import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { ScopePicker } from "../../components/iam/ScopePicker";
import { ArrowLeft, ShieldCheck, Monitor, Activity, KeyRound, UserCog, Download, Ban, Play, UserX, Mail } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700", invited: "bg-blue-50 text-blue-700",
  suspended: "bg-amber-50 text-amber-700", deactivated: "bg-gray-100 text-gray-500",
};

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState({});
  const [scope, setScope] = useState({});
  const [roleId, setRoleId] = useState("");
  const [saving, setSaving] = useState(false);
  const [offboard, setOffboard] = useState(null);
  const [successor, setSuccessor] = useState("");

  const load = useCallback(() => {
    api.get(`/iam/users/${id}`).then((r) => {
      setData(r.data);
      const u = r.data.user;
      setProfile({ first_name: u.first_name || "", last_name: u.last_name || "", phone: u.phone || "",
        job_title: u.job_title || "", department: u.department || "", language: u.language || "de",
        manager_id: u.manager_id || "" });
      setScope(u.scope || {});
      setRoleId(u.role_id || "");
    }).catch((e) => toast.error(errDetail(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/iam/roles").then((r) => setRoles(r.data || [])).catch(() => {});
    api.get("/iam/users?page_size=200").then((r) => setUsers(r.data.items || [])).catch(() => {});
  }, []);

  const save = async (payload, msg) => {
    setSaving(true);
    try {
      await api.put(`/iam/users/${id}`, payload);
      toast.success(msg || `${t("save")} ✓`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const action = async (path, body) => {
    try {
      await api.post(`/iam/users/${id}/${path}`, body || {});
      toast.success("✓");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "successor_required") {
        setOffboard(d);
        return;
      }
      toast.error(errDetail(e));
    }
  };

  const gdpr = async () => {
    const { data: exp } = await api.get(`/iam/users/${id}/gdpr-export`);
    const blob = new Blob([JSON.stringify(exp, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gdpr-${id}.json`;
    a.click();
    toast.success("✓");
  };

  if (!data) return <p className="text-sm text-gray-400">{t("loading")}</p>;
  const u = data.user;
  const r = data.resolved;

  return (
    <div className="space-y-4" data-testid="iam-user-detail">
      <button onClick={() => navigate("/iam/users")} className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#1B2A4A]"
        data-testid="user-back-btn"><ArrowLeft className="h-3.5 w-3.5" />{t("iam_users")}</button>

      <div className="rounded-2xl bg-[#1B2A4A] grain p-5 sm:p-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-[#E8871E]/25 blur-3xl" />
        <div className="relative flex flex-wrap items-center gap-4">
          <div className="h-14 w-14 rounded-xl bg-[#E8871E] flex items-center justify-center text-[#1B2A4A] text-xl font-bold">
            {(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-[180px]">
            <h1 className="text-white text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }} data-testid="user-detail-name">{u.name}</h1>
            <p className="text-white/60 text-sm">{u.email}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge className={`${STATUS_STYLE[u.status] || "bg-gray-100"} hover:bg-inherit text-[10px]`} data-testid="user-detail-status">
                {t(`iam_status_${u.status || "active"}`)}
              </Badge>
              <Badge className="bg-white/10 text-white hover:bg-white/10 text-[10px]">{r.role?.name}</Badge>
              {u.auth_method === "sso" && <Badge className="bg-blue-500/20 text-blue-100 hover:bg-inherit text-[10px]">SSO</Badge>}
              {data.mfa_enabled && <Badge className="bg-emerald-500/20 text-emerald-100 hover:bg-inherit text-[10px]">MFA</Badge>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {u.status === "invited" && (
              <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => action("resend-invite")} data-testid="user-resend-invite-btn">
                <Mail className="h-3.5 w-3.5 mr-1" />{t("iam_resend_invite")}
              </Button>
            )}
            {u.status === "suspended" ? (
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => action("reactivate")}
                data-testid="user-reactivate-btn"><Play className="h-3.5 w-3.5 mr-1" />{t("iam_reactivate")}</Button>
            ) : u.status !== "deactivated" && (
              <Button size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => action("suspend", { reason: "" })} data-testid="user-suspend-btn">
                <Ban className="h-3.5 w-3.5 mr-1" />{t("iam_suspend")}
              </Button>
            )}
            {u.status !== "deactivated" && (
              <Button size="sm" className="bg-red-500/90 hover:bg-red-600 text-white"
                onClick={() => action("deactivate", {})} data-testid="user-deactivate-btn">
                <UserX className="h-3.5 w-3.5 mr-1" />{t("iam_deactivate")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="user-tab-profile"><UserCog className="h-3.5 w-3.5 mr-1" />{t("iam_tab_profile")}</TabsTrigger>
          <TabsTrigger value="perms" data-testid="user-tab-permissions"><ShieldCheck className="h-3.5 w-3.5 mr-1" />{t("iam_tab_permissions")}</TabsTrigger>
          <TabsTrigger value="scope" data-testid="user-tab-scope"><KeyRound className="h-3.5 w-3.5 mr-1" />{t("iam_tab_scope")}</TabsTrigger>
          <TabsTrigger value="activity" data-testid="user-tab-activity"><Activity className="h-3.5 w-3.5 mr-1" />{t("iam_tab_activity")}</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="user-tab-sessions"><Monitor className="h-3.5 w-3.5 mr-1" />{t("iam_tab_sessions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-4 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["first_name", "first_name"], ["last_name", "last_name"], ["phone", "iam_phone"],
              ["job_title", "iam_job_title"], ["department", "iam_department"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{t(label)}</Label>
                <Input value={profile[k] || ""} onChange={(e) => setProfile((p) => ({ ...p, [k]: e.target.value }))}
                  data-testid={`user-${k}-input`} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs">{t("iam_manager")}</Label>
              <Select value={profile.manager_id || "none"} onValueChange={(v) => setProfile((p) => ({ ...p, manager_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="user-manager-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">–</SelectItem>
                  {users.filter((x) => x.id !== id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving}
                onClick={() => save(profile)} data-testid="user-save-profile-btn">{t("save")}</Button>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap gap-2 items-center">
            <p className="text-xs text-gray-500 flex-1">{t("iam_compliance_help")}</p>
            <Button size="sm" variant="outline" onClick={gdpr} data-testid="user-gdpr-btn">
              <Download className="h-3.5 w-3.5 mr-1" />{t("iam_gdpr_export")}
            </Button>
            {data.mfa_enabled && (
              <Button size="sm" variant="outline" data-testid="user-mfa-disable-btn"
                onClick={async () => { await api.post("/auth/mfa/disable", { user_id: id }); toast.success("✓"); load(); }}>
                {t("iam_disable_mfa")}
              </Button>
            )}
            {u.status === "deactivated" && !u.anonymized && (
              <Button size="sm" variant="outline" className="text-red-600" data-testid="user-anonymize-btn"
                onClick={async () => { await api.post(`/iam/users/${id}/anonymize`); toast.success("✓"); load(); }}>
                {t("iam_anonymize")}
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="perms" className="pt-4 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="space-y-1 flex-1 min-w-[220px]">
                <Label className="text-xs">{t("role")}</Label>
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving || roleId === u.role_id}
                onClick={() => save({ role_id: roleId })} data-testid="user-save-role-btn">{t("save")}</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="text-[10px]">{t("account_visibility")}: {r.account_visibility}</Badge>
              <Badge variant="outline" className="text-[10px]">{t("iam_account_count")}: {String(r.account_count)}</Badge>
              {Object.entries(r.flags || {}).filter(([, v]) => v).map(([k]) => (
                <Badge key={k} className="bg-emerald-50 text-emerald-700 hover:bg-inherit text-[10px]">{t(k)}</Badge>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="user-resolved-perms">
            <p className="text-sm font-semibold text-[#1B2A4A] mb-2">{t("iam_resolved_perms")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {(r.can || []).map((c) => (
                <div key={c.module} className="text-xs flex gap-2 border-b border-gray-50 py-1" data-testid={`resolved-${c.module}`}>
                  <span className="font-medium min-w-[110px]">{t(c.module)}</span>
                  <span className="text-gray-500">{c.actions.map((a) => t(`act_${a}`)).join(" · ")}</span>
                </div>
              ))}
              {!(r.can || []).length && <p className="text-xs text-gray-400">–</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="scope" className="pt-4 space-y-3">
          <ScopePicker value={scope} onChange={setScope} testid="user-scope-picker" />
          <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
            <p className="text-xs text-gray-500">{t("iam_effective")}: {(r.scope?.effective_plants || []).map((p) => p.code).join(", ") || (r.scope?.all_plants ? t("iam_all_plants") : "–")}</p>
            <p className="text-xs text-gray-500">{t("iam_owned_accounts")}: {data.owned_accounts}</p>
            <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving}
              onClick={() => save({ scope })} data-testid="user-save-scope-btn">{t("save")}</Button>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="pt-4 space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50" data-testid="user-logins">
            <p className="px-4 py-2 text-xs font-semibold text-[#1B2A4A]">{t("iam_login_audit")}</p>
            {(data.logins || []).map((l) => (
              <div key={l.id} className="px-4 py-2 text-xs flex flex-wrap gap-2">
                <span className={l.success ? "text-emerald-600" : "text-red-600"}>{l.success ? "✓" : "✕"}</span>
                <span>{l.method}</span><span className="text-gray-400">{l.reason}</span>
                <span className="text-gray-400 font-mono">{l.ip}</span>
                <span className="ml-auto text-gray-400 font-mono">{(l.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
            {!(data.logins || []).length && <p className="px-4 py-3 text-xs text-gray-400">–</p>}
          </div>
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50" data-testid="user-activity">
            <p className="px-4 py-2 text-xs font-semibold text-[#1B2A4A]">{t("audit_trail")}</p>
            {(data.activity || []).map((a) => (
              <div key={a.id} className="px-4 py-2 text-xs flex flex-wrap gap-2">
                <span className="font-medium">{a.entity_type}</span><span className="text-gray-500">{a.action}</span>
                <span className="ml-auto text-gray-400 font-mono">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
            {!(data.activity || []).length && <p className="px-4 py-3 text-xs text-gray-400">{t("iam_activity_none")}</p>}
          </div>
        </TabsContent>

        <TabsContent value="sessions" className="pt-4">
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50" data-testid="user-sessions">
            {(data.sessions || []).map((s) => (
              <div key={s.id} className="px-4 py-2.5 text-xs flex flex-wrap items-center gap-2" data-testid={`session-${s.id}`}>
                <Badge variant="outline" className="text-[10px]">{s.method}</Badge>
                <span className="font-mono">{s.ip || "–"}</span>
                <span className="text-gray-400 truncate max-w-[220px]">{s.user_agent}</span>
                <span className="text-gray-400 font-mono">{(s.last_seen || "").slice(0, 16).replace("T", " ")}</span>
                {s.revoked
                  ? <Badge className="bg-gray-100 text-gray-500 hover:bg-inherit text-[10px] ml-auto">{s.revoked_reason || "revoked"}</Badge>
                  : <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]" data-testid={`revoke-session-${s.id}`}
                      onClick={async () => { await api.delete(`/iam/sessions/${s.id}`); toast.success("✓"); load(); }}>
                      {t("iam_revoke")}
                    </Button>}
              </div>
            ))}
            {!(data.sessions || []).length && <p className="px-4 py-4 text-xs text-gray-400">{t("iam_sessions_none")}</p>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!offboard} onOpenChange={(o) => !o && setOffboard(null)}>
        <DialogContent className="max-w-md" data-testid="offboard-dialog">
          <DialogHeader>
            <DialogTitle>{t("iam_successor_required")}</DialogTitle>
            <DialogDescription>{offboard?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-40 overflow-y-auto text-xs divide-y divide-gray-50 rounded-md border border-gray-100">
              {(offboard?.accounts || []).map((a) => (
                <p key={a.id} className="px-3 py-1.5">{a.name}</p>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("iam_choose_successor")}</Label>
              <Select value={successor} onValueChange={setSuccessor}>
                <SelectTrigger data-testid="offboard-successor-select"><SelectValue placeholder="–" /></SelectTrigger>
                <SelectContent>
                  {users.filter((x) => x.id !== id && x.status === "active").map((x) => (
                    <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-red-500 hover:bg-red-600 text-white" disabled={!successor}
              data-testid="offboard-confirm-btn"
              onClick={async () => { setOffboard(null); await action("deactivate", { default_successor: successor }); }}>
              {t("iam_deactivate")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
